<template>
  <section class="event-edit-shell">
    <article class="glass-surface workspace-panel event-edit-panel">
      <div class="event-edit-hero">
        <div class="event-edit-hero__copy">
          <span class="workspace-kicker">Editar evento</span>
          <h2 class="workspace-title">{{ heroTitle }}</h2>
          <p class="workspace-copy">
            Ajusta identidad, lugar, fechas y salida desde una sola vista. La idea es que se sienta como seguir creando tu evento,
            pero afinando lo que ya existe.
          </p>

          <div class="workspace-badge-row workspace-badge-row--compact event-edit-hero__badges">
            <span class="workspace-status-pill" :class="statusPillClass">{{ eventStatusLabel }}</span>
            <span class="workspace-badge"><i class="fas fa-calendar-alt"></i>{{ previewDateRange }}</span>
            <span class="workspace-badge"><i class="fas fa-map-marker-alt"></i>{{ previewVenueLabel }}</span>
            <span class="workspace-badge"><i class="fas fa-shapes"></i>{{ previewCategoryLabel }}</span>
          </div>

          <div class="workspace-actions workspace-actions--compact event-edit-banner__actions">
            <button class="workspace-button workspace-button--small" type="button" @click="triggerBannerPicker">
              <i class="fas fa-image"></i>
              <span>{{ bannerPreview ? 'Cambiar portada' : 'Subir portada' }}</span>
            </button>

            <button v-if="bannerPreview" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="clearBanner">
              <i class="fas fa-xmark"></i>
              <span>Quitar</span>
            </button>
          </div>
        </div>

        <div class="event-edit-hero__media">
          <input ref="bannerInput" class="event-edit-file-input" type="file" accept="image/*" @change="onBannerChange" />

          <button
            class="event-edit-banner"
            :class="{ 'has-image': Boolean(bannerPreview), 'is-dragging': dragBannerActive, 'has-soft-fill': bannerPreviewUsesSoftFill }"
            :style="bannerPreviewFillStyle"
            type="button"
            @click="triggerBannerPicker"
            @dragover.prevent="handleBannerDrag"
            @dragleave.prevent="handleBannerDragLeave"
            @drop.prevent="handleBannerDrop"
          >
            <img
              v-if="bannerPreview"
              :src="bannerPreview"
              :alt="form.name || event.name || 'Preview del evento'"
              class="event-edit-banner__image"
              @load="updateBannerPreviewAspect"
              @error="resetBannerPreviewAspect"
            />

            <div v-else class="event-edit-banner__placeholder">
              <i class="fas fa-image"></i>
              <strong>Sube la portada</strong>
              <small>PNG o JPG menor a 10 MB. En mobile, si no es cuadrada, se muestra completa con fondo difuminado.</small>
            </div>

            <div class="event-edit-banner__overlay"></div>
            <div class="event-edit-banner__copy">
              <span>{{ form.name || event.name || 'Tu evento' }}</span>
              <small>{{ bannerPreview ? 'Haz click para reemplazarla' : 'Se vera en el hero y en el checkout' }}</small>
            </div>
          </button>
        </div>
      </div>

      <div v-if="event.review_note" class="event-edit-review-note" :class="reviewNoteToneClass">
        <span>Feedback</span>
        <strong>{{ reviewNoteTitle }}</strong>
        <p>{{ event.review_note }}</p>
      </div>

      <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
      <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

      <form class="workspace-form event-edit-form" @submit.prevent="submitChanges">
        <section class="event-edit-card">
          <div class="event-edit-card__head">
            <div>
              <small>Identidad</small>
              <strong>Lo que la gente va a ver primero</strong>
            </div>
            <span class="event-edit-card__eyebrow">Nombre, descripcion y categoria</span>
          </div>

          <div class="workspace-form workspace-form--two event-edit-grid">
            <label class="workspace-field workspace-field--wide">
              <span>Nombre</span>
              <input v-model="form.name" class="workspace-input" type="text" placeholder="Neon Ritual 2026" required />
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Descripcion</span>
              <textarea
                v-model="form.description"
                class="workspace-input workspace-input--textarea"
                rows="5"
                placeholder="Cuenta de que trata el evento, para quien es y por que vale la pena ir."
              ></textarea>
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Categoria</span>
              <select v-model="form.category" class="workspace-input workspace-select" :disabled="loadingOptions">
                <option value="">{{ loadingOptions ? 'Cargando categorias...' : 'Selecciona una categoria' }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
              </select>
            </label>
          </div>
        </section>

        <section class="event-edit-card">
          <div class="event-edit-card__head">
            <div>
              <small>Lugar</small>
              <strong>Ciudad clara, venue listo</strong>
            </div>
            <span class="event-edit-card__eyebrow">Mantiene el checkout bien orientado</span>
          </div>

          <div class="workspace-form workspace-form--two event-edit-grid">
            <label class="workspace-field">
              <span>Pais</span>
              <select v-model="form.country" class="workspace-input workspace-select" :disabled="loadingOptions" @change="handleCountryChange">
                <option value="">{{ loadingOptions ? 'Cargando paises...' : 'Selecciona un pais' }}</option>
                <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Ciudad</span>
              <select v-model="form.city" class="workspace-input workspace-select" :disabled="loadingOptions || !form.country" @change="handleCityChange">
                <option value="">{{ cityPlaceholder }}</option>
                <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
              </select>
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Venue</span>
              <select v-model="form.venue" class="workspace-input workspace-select" :disabled="loadingOptions || loadingVenues || !form.city">
                <option value="">{{ venuePlaceholder }}</option>
                <option v-for="venue in filteredVenues" :key="venue.id" :value="venue.id">{{ venue.location_label ? `${venue.name} - ${venue.location_label}` : venue.name }}</option>
              </select>
              <small class="workspace-field__note">{{ venueHelperText }}</small>

              <div class="event-edit-location-helper">
                <button class="event-edit-link-button" type="button" :disabled="!form.city || savingVenue" @click="toggleVenueComposer()">
                  {{ showVenueComposer ? 'Cerrar creador de venue' : 'Crear venue nuevo' }}
                </button>
              </div>
            </label>

            <div v-if="showVenueComposer" class="workspace-field workspace-field--wide event-edit-venue-composer">
              <div class="workspace-form workspace-form--two event-edit-venue-composer__grid">
                <label class="workspace-field workspace-field--wide">
                  <span>Nombre del venue</span>
                  <input v-model.trim="venueDraft.name" class="workspace-input" type="text" placeholder="Club, rooftop, teatro..." />
                </label>

                <label class="workspace-field workspace-field--wide">
                  <span>Direccion</span>
                  <input v-model.trim="venueDraft.address" class="workspace-input" type="text" placeholder="Direccion o referencia" />
                </label>
              </div>

              <div class="workspace-actions workspace-actions--compact event-edit-venue-composer__actions">
                <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="toggleVenueComposer(false)">
                  <span>Cancelar</span>
                </button>
                <button class="workspace-button workspace-button--small workspace-button--primary" type="button" :disabled="savingVenue || !venueDraft.name" @click="createVenue">
                  <span>{{ savingVenue ? 'Creando...' : 'Guardar venue' }}</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="event-edit-card">
          <div class="event-edit-card__head">
            <div>
              <small>Salida</small>
              <strong>Fechas y forma de acceso</strong>
            </div>
            <span class="event-edit-card__eyebrow">Listo para afinar antes de lanzar</span>
          </div>

          <div class="event-edit-visibility-strip">
            <div class="event-edit-visibility-copy">
              <small>Visibilidad</small>
              <strong>{{ visibilityLabel }}</strong>
              <span>{{ visibilityHint }}</span>
            </div>

            <button
              class="event-edit-link-button event-edit-link-button--pill"
              :class="{ 'is-active': isHiddenVisibility }"
              type="button"
              @click="setHiddenVisibility"
            >
              <i class="fas fa-eye-slash"></i>
              <span>{{ isHiddenVisibility ? 'Oculto por ahora' : 'Ocultar por ahora' }}</span>
            </button>
          </div>

          <div class="event-edit-mode-cards">
            <button class="event-edit-mode-card" :class="{ 'is-active': isOpenAccess }" type="button" @click="applyAccessPreset('open')">
              <span class="event-edit-mode-card__icon"><i class="fas fa-globe-americas"></i></span>
              <span class="event-edit-mode-card__badge">Open</span>
              <strong>Abierto</strong>
              <p>Cualquiera puede comprar sin codigo. Ideal para ventas abiertas y checkout directo.</p>
            </button>

            <button class="event-edit-mode-card" :class="{ 'is-active': isControlledAccess }" type="button" @click="applyAccessPreset('controlled')">
              <span class="event-edit-mode-card__icon event-edit-mode-card__icon--alt"><i class="fas fa-key"></i></span>
              <span class="event-edit-mode-card__badge event-edit-mode-card__badge--alt">Controlled</span>
              <strong>Con codigo</strong>
              <p>La venta pide codigo. Ideal para invitados, promotores o accesos controlados.</p>
            </button>
          </div>

          <div class="workspace-form workspace-form--two event-edit-grid">
            <label class="workspace-field">
              <span>Inicio</span>
              <input v-model="form.start_date" class="workspace-input" type="datetime-local" required />
            </label>

            <label class="workspace-field">
              <span>Fin</span>
              <input v-model="form.end_date" class="workspace-input" type="datetime-local" required />
            </label>

            <div class="workspace-field workspace-field--wide event-edit-time-note">
              <small class="workspace-field__note">Usaremos la hora local del evento segun su zona horaria.</small>
            </div>

            <div class="workspace-field workspace-field--wide">
              <span>Checkout</span>
              <label class="workspace-inline-check">
                <input v-model="form.show_end_date" type="checkbox" />
                <span>Mostrar inicio y cierre en el checkout</span>
              </label>
            </div>

            <div class="workspace-field workspace-field--wide">
              <span>Estado de salida</span>
              <div class="event-edit-launch-card" :class="launchCardClass">
                <div>
                  <strong>{{ launchHeadline }}</strong>
                  <p>{{ launchCopy }}</p>
                </div>

                <button v-if="!isLiveEvent" class="workspace-button workspace-button--primary" type="button" :disabled="launching || !canLaunchEvent" @click="launchEvent">
                  <i class="fas fa-rocket"></i>
                  <span>{{ launching ? 'Lanzando...' : 'Lanzar evento' }}</span>
                </button>

                <small v-else class="event-edit-launch-card__hint">Si necesitas bajarlo por un rato, usa <strong>Ocultar por ahora</strong>.</small>
              </div>
            </div>
          </div>

          <section class="event-edit-optional">
            <button class="event-edit-optional-toggle" type="button" @click="advancedOpen = !advancedOpen">
              <span>
                <strong>Opcional</strong>
                <small>Configuracion avanzada y ajustes adicionales del evento</small>
              </span>
              <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>

            <div v-if="advancedOpen" class="event-edit-optional-stack">
              <label class="workspace-field workspace-field--wide event-edit-optional-card">
                <span>Visibilidad</span>

                <div class="event-edit-cta-presets">
                  <button
                    v-for="option in visibilityOptions"
                    :key="option.key"
                    class="event-edit-cta-preset"
                    :class="{ 'is-active': selectedVisibilityOption === option.key }"
                    type="button"
                    @click="selectVisibilityOption(option.key)"
                  >
                    <strong>{{ option.label }}</strong>
                    <small>{{ option.hint }}</small>
                  </button>
                </div>

                <small class="workspace-field__note">Privado no sale en home. Si lo ocultas, queda como preview interno.</small>
              </label>

              <label class="workspace-field workspace-field--wide event-edit-optional-card">
                <span>CTA publico</span>

                <div class="event-edit-cta-presets">
                  <button
                    v-for="option in publicCtaPresetOptions"
                    :key="option.key"
                    class="event-edit-cta-preset"
                    :class="{ 'is-active': selectedPublicCtaOption === option.key }"
                    type="button"
                    @click="selectPublicCtaOption(option.key)"
                  >
                    <strong>{{ option.label }}</strong>
                    <small>{{ option.hint }}</small>
                  </button>
                </div>

                <span>{{ selectedPublicCtaOption === 'custom' ? 'Texto personalizado' : 'Texto actual' }}</span>
                <input
                  v-model.trim="form.public_cta_label"
                  class="workspace-input event-edit-cta-input"
                  type="text"
                  maxlength="40"
                  :disabled="selectedPublicCtaOption !== 'custom'"
                  :placeholder="selectedPublicCtaOption === 'custom' ? 'Escribe un CTA corto' : 'Selecciona Personalizado para escribir'"
                />
                <small class="workspace-field__note">{{ publicCtaFieldNote }}</small>
              </label>

              <div v-if="canEditFomoOriginal" class="workspace-field workspace-field--wide event-edit-optional-card">
                <span>Sello editorial FOMO</span>
                <label class="workspace-inline-check">
                  <input v-model="form.is_fomo_original" type="checkbox" />
                  <span>Marcar como FOMO Original</span>
                </label>
                <small class="workspace-field__note">Solo admins FOMO pueden activar este sello en las cards publicas.</small>
              </div>
            </div>
          </section>
        </section>

        <div class="event-edit-footer">
          <div class="event-edit-footer__summary">
            <small>Vista rapida</small>
            <strong>{{ form.name || event.name || 'Tu evento' }}</strong>
            <span>{{ previewCategoryLabel }} · {{ previewVenueLabel }} · {{ previewDateRange }}</span>
          </div>

          <div class="workspace-actions workspace-actions--compact event-edit-footer__actions">
            <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" :disabled="saving || !isDirty" @click="resetForm">
              <i class="fas fa-rotate-left"></i>
              <span>Restaurar</span>
            </button>

            <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving || !canSubmit">
              <i class="fas fa-save"></i>
              <span>{{ saving ? 'Guardando...' : 'Guardar cambios' }}</span>
            </button>
          </div>
        </div>
      </form>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import { classifyBannerShape } from '@/common/banner-image.js'
import { formatEventInputPreview, resolveCountryTimeZone, toEventDateTimeInput, toUtcIsoFromEventDateTimeInput } from '@/common/event-timezone.js'

const FIELD_LABELS = {
  name: 'nombre',
  description: 'descripcion',
  banner_url: 'portada',
  start_date: 'fecha de inicio',
  end_date: 'fecha de cierre',
  timezone: 'zona horaria',
  country: 'pais',
  city: 'ciudad',
  venue: 'venue',
  category: 'categoria',
  type: 'salida',
  access: 'acceso',
  public_cta_label: 'CTA publico',
  show_end_date: 'fecha final visible',
}

const CTA_PRESET_OPTIONS = [
  {
    key: 'automatic',
    label: 'Automatico',
    value: '',
    hint: 'Usa Comprar ahora',
  },
  {
    key: 'reservar',
    label: 'Reservar',
    value: 'Reservar',
    hint: 'Reserva directa',
  },
  {
    key: 'quiero-ir',
    label: 'Quiero ir',
    value: 'Quiero ir',
    hint: 'Mas emocional',
  },
  {
    key: 'custom',
    label: 'Personalizado',
    value: null,
    hint: 'Desbloquea el input',
  },
]

const VISIBILITY_OPTIONS = [
  {
    key: 'public',
    label: 'Publico',
    hint: 'Visible normal y sale en home.',
  },
  {
    key: 'private',
    label: 'Evento privado',
    hint: 'No sale en home, pero sigue abriendo por link directo.',
  },
]

const isPublishedEventType = (value) => [1, 2].includes(Number(value))

function isLocalRangeInvalid(startValue, endValue) {
  const start = new Date(startValue)
  const end = new Date(endValue)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false
  }

  return end < start
}

export default {
  name: 'EventEditPanel',

  props: {
    event: {
      type: Object,
      required: true,
    },
    eventId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      countries: [],
      categories: [],
      cities: [],
      venues: [],
      venuesByCity: {},
      venueRequestToken: 0,
      cityRequestToken: 0,
      loadingOptions: true,
      loadingVenues: false,
      savingVenue: false,
      saving: false,
      launching: false,
      errorMessage: '',
      successMessage: '',
      dragBannerActive: false,
      bannerPreview: '',
      bannerPreviewAspect: 'unknown',
      bannerObjectUrl: '',
      removeBanner: false,
      syncedSignature: '',
      lastHydratedSignature: '',
      advancedOpen: false,
      selectedPublicCtaOption: 'automatic',
      publicCtaPresetOptions: CTA_PRESET_OPTIONS,
      visibilityOptions: VISIBILITY_OPTIONS,
      showVenueComposer: false,
      venueDraft: {
        name: '',
        address: '',
      },
      form: {
        name: '',
        description: '',
        category: '',
        type: 1,
        access: 1,
        public_cta_label: '',
        start_date: '',
        end_date: '',
        timezone: '',
        country: '',
        city: '',
        venue: '',
        show_end_date: false,
        is_fomo_original: false,
        banner: null,
      },
    }
  },

  computed: {
    selectedCategory() {
      return this.categories.find((category) => Number(category.id) === Number(this.form.category)) || null
    },

    selectedCountry() {
      return this.countries.find((country) => Number(country.id) === Number(this.form.country)) || null
    },

    selectedCity() {
      return this.cities.find((city) => Number(city.id) === Number(this.form.city)) || null
    },

    selectedVenue() {
      return this.venues.find((venue) => Number(venue.id) === Number(this.form.venue)) || null
    },

    filteredVenues() {
      return this.venues
    },

    cityPlaceholder() {
      if (!this.form.country) {
        return 'Selecciona un pais primero'
      }

      return this.loadingOptions ? 'Cargando ciudades...' : 'Selecciona una ciudad'
    },

    venuePlaceholder() {
      if (this.loadingOptions || this.loadingVenues) {
        return 'Cargando venues...'
      }

      if (!this.form.city) {
        return 'Selecciona una ciudad primero'
      }

      if (this.venues.length === 0 || this.filteredVenues.length === 0) {
        return 'No hay venues cargados para esa ciudad'
      }

      return 'Selecciona un venue'
    },

    venueHelperText() {
      if (!this.form.city) {
        return 'Elige la ciudad primero para cargar solo los venues que correspondan.'
      }

      if (this.loadingVenues) {
        return `Cargando venues para ${this.selectedCity ? this.selectedCity.name : 'la ciudad'}...`
      }

      if (this.venues.length === 0) {
        return `No hay venues cargados para ${this.selectedCity ? this.selectedCity.name : 'esa ciudad'}.`
      }

      return `${this.filteredVenues.length} venues disponibles para ${this.selectedCity ? this.selectedCity.name : 'la ciudad seleccionada'}`
    },

    previewDateRange() {
      if (!this.form.start_date && !this.form.end_date) {
        return 'Por definir'
      }

      const start = this.formatPreviewDate(this.form.start_date)
      const end = this.formatPreviewDate(this.form.end_date)

      if (start && end) {
        return `${start} - ${end}`
      }

      return start || end || 'Por definir'
    },

    previewVenueLabel() {
      const activeVenue = this.selectedVenue || this.event.venue_detail || this.event.venue || null
      if (activeVenue && activeVenue.name) {
        return activeVenue.name
      }

      return 'Venue pendiente'
    },

    previewCategoryLabel() {
      const activeCategory = this.selectedCategory || this.event.category_detail || this.event.category || null
      if (activeCategory && activeCategory.name) {
        return activeCategory.name
      }

      return 'Categoria por definir'
    },

    bannerPreviewUsesSoftFill() {
      return Boolean(this.bannerPreview && this.bannerPreviewAspect !== 'square' && this.bannerPreviewAspect !== 'unknown')
    },

    bannerPreviewFillStyle() {
      if (!this.bannerPreviewUsesSoftFill) {
        return null
      }

      return {
        '--event-edit-banner-fill': `url("${this.bannerPreview}")`,
      }
    },

    isOpenAccess() {
      return Number(this.form.access) === 1
    },

    isControlledAccess() {
      return Number(this.form.access) !== 1
    },

    canEditFomoOriginal() {
      return Boolean(this.event && this.event.can_edit_fomo_original)
    },

    isHiddenVisibility() {
      return Number(this.form.type) === 3
    },

    selectedVisibilityOption() {
      return Number(this.form.type) === 2 ? 'private' : 'public'
    },

    visibilityLabel() {
      if (this.isHiddenVisibility) {
        return 'Oculto por ahora'
      }

      if (Number(this.form.type) === 2) {
        return 'Evento privado'
      }

      return 'Abierto al publico'
    },

    visibilityHint() {
      if (this.isHiddenVisibility) {
        return 'No se muestra al publico y solo queda disponible como preview interno.'
      }

      if (Number(this.form.type) === 2) {
        return 'No sale en home, pero sigue disponible por link directo.'
      }

      return 'Visible normalmente y aparece en home.'
    },

    publicCtaFieldNote() {
      if (this.selectedPublicCtaOption === 'custom') {
        return 'Personaliza el boton principal de la web. Si lo dejas vacio, usa Comprar ahora. Max 40 caracteres.'
      }

      if (this.selectedPublicCtaOption === 'automatic') {
        return 'En automatico, el boton principal usa Comprar ahora.'
      }

      return `Usaremos "${this.form.public_cta_label || 'Comprar ahora'}" en el boton principal.`
    },

    isDirty() {
      return this.captureFormSignature() !== this.syncedSignature
    },

    canSubmit() {
      return Boolean(
        this.form.name &&
        this.form.category &&
        this.form.country &&
        this.form.city &&
        this.form.start_date &&
        this.form.end_date &&
        this.form.timezone
      )
    },

    canLaunchEvent() {
      return Boolean(this.event && this.event.id && this.event.review_status === 'approved' && Number(this.event.type) === 3)
    },

    isLiveEvent() {
      return Boolean(this.event && this.event.review_status === 'approved' && isPublishedEventType(this.event.type))
    },

    eventStatusLabel() {
      if (!this.event || !this.event.id) {
        return 'Cargando evento'
      }

      if (this.event.review_status === 'pending') {
        return 'Revision pendiente'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Cambios solicitados'
      }

      if (this.event.review_status === 'rejected') {
        return 'Revision rechazada'
      }

      if (Number(this.event.type) === 3) {
        return 'Oculto por ahora'
      }

      if (Number(this.event.type) === 2) {
        return 'Venta privada'
      }

      return 'Venta activa'
    },

    statusPillClass() {
      if (this.event.review_status === 'approved') {
        return 'is-active'
      }

      if (this.event.review_status === 'changes_requested' || this.event.review_status === 'pending') {
        return 'is-warning'
      }

      return 'is-danger'
    },

    heroTitle() {
      if (this.event.review_status === 'changes_requested') {
        return 'Haz los ajustes y vuelve a dejarlo listo.'
      }

      if (this.event.review_status === 'approved' && Number(this.event.type) === 3) {
        return 'Todo listo para afinar antes del lanzamiento.'
      }

      if (this.event.review_status === 'approved' && Number(this.event.type) === 2) {
        return 'Tu evento sigue activo en privado.'
      }

      if (isPublishedEventType(this.event.type)) {
        return 'Afina tu evento sin perder su estilo.'
      }

      return 'Pon tu evento a punto en una sola vista.'
    },

    reviewNoteTitle() {
      if (this.event.review_status === 'changes_requested') {
        return 'Cambios pedidos por admin'
      }

      if (this.event.review_status === 'rejected') {
        return 'Motivo del rechazo'
      }

      return 'Nota del review'
    },

    reviewNoteToneClass() {
      if (this.event.review_status === 'changes_requested') {
        return 'is-warning'
      }

      if (this.event.review_status === 'rejected') {
        return 'is-danger'
      }

      return 'is-neutral'
    },

    launchHeadline() {
      if (!this.event || !this.event.id) {
        return 'Cargando estado de lanzamiento'
      }

      if (this.event.review_status === 'pending') {
        return 'Esperando aprobacion'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Haz ajustes y vuelve a dejarlo listo'
      }

      if (this.event.review_status === 'rejected') {
        return 'Revision rechazada'
      }

      if (Number(this.event.type) === 3) {
        return 'Aprobado y listo para lanzar'
      }

      if (Number(this.event.type) === 2) {
        return 'Evento privado activo'
      }

      return 'Evento ya lanzado'
    },

    launchCopy() {
      if (this.event.review_status === 'pending') {
        return 'Mientras el equipo revisa la salida, aqui puedes afinar la presentacion del evento.'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Corrige la info clave y vuelve a dejarlo listo para una nueva revision.'
      }

      if (this.event.review_status === 'rejected') {
        return 'Conviene revisar el feedback antes de volver a mover este evento.'
      }

      if (Number(this.event.type) === 3) {
        return 'Ya fue aprobado por admin. Cuando quieras, lo puedes dejar visible con Lanzar evento.'
      }

      if (Number(this.event.type) === 2) {
        return 'No sale en home, pero sigue disponible por link directo.'
      }

      if (Number(this.event.type) === 1) {
        return 'El evento ya esta visible para compra. Si quieres bajarlo un rato, usa Ocultar por ahora y guarda los cambios.'
      }

      return 'Ajusta lo que necesites antes de volver a mover la salida del evento.'
    },

    launchCardClass() {
      if (this.event.review_status === 'approved' && Number(this.event.type) === 3) {
        return 'is-ready'
      }

      if (isPublishedEventType(this.event.type)) {
        return 'is-live'
      }

      return 'is-waiting'
    },
  },

  created() {
    this.bootstrapOptions()
  },

  beforeUnmount() {
    this.revokeBannerPreview()
  },

  watch: {
    event: {
      immediate: true,
      deep: true,
      handler(value) {
        const nextSignature = this.computeEventEditableSignature(value)
        if (nextSignature === this.lastHydratedSignature) {
          return
        }

        this.lastHydratedSignature = nextSignature
        this.hydrateFormFromEvent(value)
      },
    },
  },

  methods: {
    normalizeCollection(payload) {
      if (Array.isArray(payload)) {
        return payload
      }

      if (payload && Array.isArray(payload.results)) {
        return payload.results
      }

      return []
    },

    normalizeVenues(payload) {
      return this.normalizeCollection(payload).map((venue) => ({
        ...venue,
        city_id: venue.city_id || (venue.city && typeof venue.city === 'object' ? venue.city.id : venue.city) || null,
        zone_id: venue.zone_id || (venue.zone && typeof venue.zone === 'object' ? venue.zone.id : venue.zone) || null,
      })).sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
    },

    normalizeCountries(payload) {
      return this.normalizeCollection(payload).sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
    },

    extractRelationId(value, fallback = '') {
      if (value == null || value === '') {
        return fallback
      }

      if (typeof value === 'object') {
        return value.id || fallback
      }

      return value
    },

    toLocalDateTime(value, source = null) {
      return toEventDateTimeInput(value, source || this.form.timezone || this.event.timezone || this.selectedCountry)
    },

    formatPreviewDate(value) {
      return formatEventInputPreview(value, this.form.timezone)
    },

    syncTimezoneSelection(force = false) {
      const countryTimezone = (this.selectedCountry && this.selectedCountry.default_timezone) || resolveCountryTimeZone(this.selectedCountry)
      if (force || !this.form.timezone) {
        this.form.timezone = countryTimezone || this.form.timezone || 'UTC'
      }
    },

    resolvePublicCtaSelection(value) {
      const normalizedValue = String(value || '').trim().toLowerCase()
      if (!normalizedValue) {
        return 'automatic'
      }

      const matchedOption = CTA_PRESET_OPTIONS.find((option) => typeof option.value === 'string' && option.value.trim().toLowerCase() === normalizedValue)
      return matchedOption ? matchedOption.key : 'custom'
    },

    selectPublicCtaOption(optionKey) {
      this.selectedPublicCtaOption = optionKey

      if (optionKey === 'custom') {
        return
      }

      const selectedOption = CTA_PRESET_OPTIONS.find((option) => option.key === optionKey)
      this.form.public_cta_label = selectedOption && typeof selectedOption.value === 'string' ? selectedOption.value : ''
    },

    computeEventEditableSignature(event) {
      const source = event || {}
      return JSON.stringify({
        name: source.name || '',
        description: source.description || '',
        type: Number(source.type || 1),
        access: Number(source.access || 1),
        public_cta_label: source.public_cta_label || '',
        timezone: source.timezone || '',
        start_date: this.toLocalDateTime(source.start_date, source.timezone || source.country_detail || source.country),
        end_date: this.toLocalDateTime(source.end_date, source.timezone || source.country_detail || source.country),
        category: this.extractRelationId(source.category_id, this.extractRelationId(source.category)),
        country: this.extractRelationId(source.country_id, this.extractRelationId(source.country)),
        city: this.extractRelationId(source.city_id, this.extractRelationId(source.city)),
        venue: this.extractRelationId(source.venue_id, this.extractRelationId(source.venue)),
        show_end_date: Boolean(source.show_end_date),
        is_fomo_original: Boolean(source.is_fomo_original),
        banner_url: source.banner_url || '',
      })
    },

    captureFormSignature() {
      return JSON.stringify({
        name: this.form.name || '',
        description: this.form.description || '',
        type: Number(this.form.type || 1),
        access: Number(this.form.access || 1),
        public_cta_label: this.form.public_cta_label || '',
        start_date: this.form.start_date || '',
        end_date: this.form.end_date || '',
        timezone: this.form.timezone || '',
        category: this.form.category || '',
        country: this.form.country || '',
        city: this.form.city || '',
        venue: this.form.venue || '',
        show_end_date: Boolean(this.form.show_end_date),
        is_fomo_original: Boolean(this.form.is_fomo_original),
        removeBanner: this.removeBanner,
        bannerFile: this.form.banner ? `${this.form.banner.name}:${this.form.banner.size}` : '',
        bannerPreview: this.bannerPreview || '',
      })
    },

    syncSelectedVenue(preferredVenueId = '') {
      const candidateId = preferredVenueId || this.form.venue

      if (candidateId && this.venues.some((venue) => Number(venue.id) === Number(candidateId))) {
        this.form.venue = candidateId
        return
      }

      this.form.venue = ''
    },

    syncSelectedCity(preferredCityId = '') {
      const candidateId = preferredCityId || this.form.city

      if (candidateId && this.cities.some((city) => Number(city.id) === Number(candidateId))) {
        this.form.city = candidateId
        return
      }

      this.form.city = ''
      this.form.venue = ''
    },

    fetchVenues(cityId, { preferredVenueId = '', force = false } = {}) {
      if (!cityId) {
        this.venues = []
        this.loadingVenues = false
        this.form.venue = ''
        return Promise.resolve([])
      }

      const cityKey = String(cityId)
      if (!force && this.venuesByCity[cityKey]) {
        this.venues = this.venuesByCity[cityKey]
        this.syncSelectedVenue(preferredVenueId)
        return Promise.resolve(this.venues)
      }

      const requestToken = this.venueRequestToken + 1
      this.venueRequestToken = requestToken
      this.loadingVenues = true

      return apiService(`/api/venues/?city_id=${encodeURIComponent(cityId)}`).then((data) => {
        if (requestToken !== this.venueRequestToken) {
          return []
        }

        const normalizedVenues = this.normalizeVenues(data)
        if (!(data && data.err)) {
          this.venuesByCity = {
            ...this.venuesByCity,
            [cityKey]: normalizedVenues,
          }
        }

        this.venues = normalizedVenues
        this.syncSelectedVenue(preferredVenueId)

        if (data && data.err) {
          this.errorMessage = this.extractErrorMessage(data)
        }

        return normalizedVenues
      }).finally(() => {
        if (requestToken === this.venueRequestToken) {
          this.loadingVenues = false
        }
      })
    },

    fetchCities(countryId, { preferredCityId = '' } = {}) {
      if (!countryId) {
        this.cities = []
        this.form.city = ''
        this.form.venue = ''
        return Promise.resolve([])
      }

      const requestToken = this.cityRequestToken + 1
      this.cityRequestToken = requestToken
      this.loadingOptions = true

      return apiService(`/api/cities/?country_id=${encodeURIComponent(countryId)}`).then((data) => {
        if (requestToken !== this.cityRequestToken) {
          return []
        }

        this.cities = this.normalizeCollection(data)
        this.syncSelectedCity(preferredCityId)
        return this.cities
      }).finally(() => {
        if (requestToken === this.cityRequestToken) {
          this.loadingOptions = false
        }
      })
    },

    bootstrapOptions() {
      this.loadingOptions = true
      Promise.allSettled([
        apiService('/api/category/'),
        apiService('/api/countries/'),
      ]).then(([categoriesResult, countriesResult]) => {
        this.categories = this.normalizeCollection(categoriesResult.value)
        this.countries = this.normalizeCountries(countriesResult.value)
        this.syncTimezoneSelection()

        const failedBuckets = []
        if (this.categories.length === 0) {
          failedBuckets.push('categorias')
        }
        if (this.countries.length === 0) {
          failedBuckets.push('paises')
        }

        if (failedBuckets.length) {
          this.errorMessage = `No pudimos cargar ${failedBuckets.join(', ')}.`
        }
      }).finally(() => {
        this.loadingOptions = false
        if (this.form.country) {
          this.fetchCities(this.form.country, { preferredCityId: this.form.city }).then(() => {
            if (this.form.city) {
              this.fetchVenues(this.form.city, { preferredVenueId: this.form.venue, force: true })
            }
          })
        }
      })
    },

    hydrateFormFromEvent(event) {
      const source = event || {}

      this.revokeBannerPreview()
      this.bannerPreviewAspect = 'unknown'
      this.form = {
        ...this.form,
        name: source.name || '',
        description: source.description || '',
        type: Number(source.type || 1),
        access: Number(source.access || 1),
        public_cta_label: source.public_cta_label || '',
        timezone: source.timezone || '',
        start_date: this.toLocalDateTime(source.start_date, source.timezone || source.country_detail || source.country),
        end_date: this.toLocalDateTime(source.end_date, source.timezone || source.country_detail || source.country),
        category: this.extractRelationId(source.category_id, this.extractRelationId(source.category)),
        country: this.extractRelationId(source.country_id, this.extractRelationId(source.country)),
        city: this.extractRelationId(source.city_id, this.extractRelationId(source.city)),
        venue: this.extractRelationId(source.venue_id, this.extractRelationId(source.venue)),
        show_end_date: Boolean(source.show_end_date),
        is_fomo_original: Boolean(source.is_fomo_original),
        banner: null,
      }
      this.bannerPreview = source.banner_url || ''
      this.removeBanner = false
      this.selectedPublicCtaOption = this.resolvePublicCtaSelection(source.public_cta_label || '')
      this.advancedOpen = false
      this.showVenueComposer = false
      this.venueDraft = { name: '', address: '' }

      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.value = ''
      }

      if (this.form.country) {
        this.syncTimezoneSelection()
        this.fetchCities(this.form.country, { preferredCityId: this.form.city }).then(() => {
          if (this.form.city) {
            this.fetchVenues(this.form.city, { preferredVenueId: this.form.venue })
          } else {
            this.venues = []
          }
        })
      } else {
        this.venues = []
      }

      this.syncedSignature = this.captureFormSignature()
    },

    handleCityChange() {
      this.form.venue = ''
      this.showVenueComposer = false
      this.fetchVenues(this.form.city)
    },

    handleCountryChange() {
      this.form.city = ''
      this.form.venue = ''
      this.showVenueComposer = false
      this.syncTimezoneSelection(true)
      this.fetchCities(this.form.country)
    },

    toggleVenueComposer(force = null) {
      if (!this.form.city) {
        return
      }

      this.showVenueComposer = force === null ? !this.showVenueComposer : force
      if (this.showVenueComposer === false) {
        this.venueDraft = { name: '', address: '' }
      }
    },

    createVenue() {
      this.errorMessage = ''

      if (!this.form.city) {
        this.errorMessage = 'Selecciona una ciudad antes de crear el venue.'
        return
      }

      if (!this.venueDraft.name) {
        this.errorMessage = 'Dale nombre al venue para crearlo.'
        return
      }

      this.savingVenue = true

      apiService('/api/venues/create/', 'POST', {
        name: this.venueDraft.name,
        address: this.venueDraft.address,
        city: this.form.city,
      }).then((data) => {
        if (!data || !data.id) {
          this.errorMessage = this.extractErrorMessage(data)
          return
        }

        const normalizedVenue = this.normalizeVenues([data])[0]
        const cityKey = String(this.form.city)
        const nextVenues = this.normalizeVenues([...this.venues, normalizedVenue])
        this.venues = nextVenues
        this.venuesByCity = {
          ...this.venuesByCity,
          [cityKey]: nextVenues,
        }
        this.form.venue = normalizedVenue.id
        this.showVenueComposer = false
        this.venueDraft = { name: '', address: '' }
      }).finally(() => {
        this.savingVenue = false
      })
    },

    selectVisibilityOption(optionKey) {
      this.form.type = optionKey === 'private' ? 2 : 1
    },

    applyAccessPreset(mode) {
      this.form.access = mode === 'open' ? 1 : 3
    },

    setHiddenVisibility() {
      this.form.type = 3
    },

    triggerBannerPicker() {
      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.click()
      }
    },

    updateBannerPreviewAspect(event) {
      const target = event && event.target ? event.target : null
      this.bannerPreviewAspect = classifyBannerShape(target && target.naturalWidth, target && target.naturalHeight)
    },

    resetBannerPreviewAspect() {
      this.bannerPreviewAspect = 'unknown'
    },

    handleBannerDrag() {
      this.dragBannerActive = true
    },

    handleBannerDragLeave() {
      this.dragBannerActive = false
    },

    handleBannerDrop(event) {
      this.dragBannerActive = false
      const file = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null
      if (!file) {
        return
      }

      this.attachBannerFile(file)
    },

    onBannerChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      this.attachBannerFile(file)
    },

    revokeBannerPreview() {
      if (this.bannerObjectUrl) {
        URL.revokeObjectURL(this.bannerObjectUrl)
        this.bannerObjectUrl = ''
      }
    },

    attachBannerFile(file) {
      if (!file.type || !file.type.startsWith('image/')) {
        this.errorMessage = 'La portada debe ser una imagen.'
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = 'La portada pesa demasiado. Usa una imagen menor a 10 MB.'
        return
      }

      this.revokeBannerPreview()
      this.bannerPreviewAspect = 'unknown'
      this.form.banner = file
      this.removeBanner = false
      this.bannerObjectUrl = URL.createObjectURL(file)
      this.bannerPreview = this.bannerObjectUrl
      this.errorMessage = ''
    },

    clearBanner() {
      this.revokeBannerPreview()
      this.dragBannerActive = false
      this.bannerPreviewAspect = 'unknown'
      this.bannerPreview = ''
      this.form.banner = null
      this.removeBanner = true
      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.value = ''
      }
    },

    appendField(formData, field, value) {
      if (typeof value === 'boolean') {
        formData.append(field, value ? 'true' : 'false')
        return
      }

      formData.append(field, value == null ? '' : value)
    },

    async buildPayload() {
      const payload = new FormData()

      this.appendField(payload, 'name', this.form.name)
      this.appendField(payload, 'description', this.form.description)
      this.appendField(payload, 'type', this.form.type)
      this.appendField(payload, 'access', this.form.access)
      this.appendField(payload, 'public_cta_label', this.form.public_cta_label || '')
      this.appendField(payload, 'start_date', toUtcIsoFromEventDateTimeInput(this.form.start_date, this.form.timezone))
      this.appendField(payload, 'end_date', toUtcIsoFromEventDateTimeInput(this.form.end_date, this.form.timezone))
      this.appendField(payload, 'timezone', this.form.timezone || '')
      this.appendField(payload, 'category', this.form.category || '')
      this.appendField(payload, 'country', this.form.country || '')
      this.appendField(payload, 'city', this.form.city || '')
      this.appendField(payload, 'venue', this.form.venue || '')
      this.appendField(payload, 'show_end_date', this.form.show_end_date)

      if (this.canEditFomoOriginal) {
        this.appendField(payload, 'is_fomo_original', this.form.is_fomo_original)
      }

      if (this.form.banner) payload.append('banner_url', this.form.banner)

      if (this.removeBanner && !this.form.banner) {
        payload.append('remove_banner', 'true')
      }

      return payload
    },

    extractErrorMessage(data, fallback = 'No pudimos guardar los cambios del evento.') {
      if (!data) {
        return fallback
      }

      if (data.err) {
        return data.err
      }

      const firstValue = typeof data === 'object' ? Object.values(data)[0] : null
      if (Array.isArray(firstValue)) {
        return firstValue[0]
      }

      return firstValue || fallback
    },

    humanizeField(fieldName) {
      return FIELD_LABELS[fieldName] || fieldName
    },

    formatFieldList(fieldNames) {
      const labels = fieldNames.map((fieldName) => this.humanizeField(fieldName))
      if (labels.length <= 1) {
        return labels[0] || 'este evento'
      }

      if (labels.length === 2) {
        return `${labels[0]} y ${labels[1]}`
      }

      return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`
    },

    buildSuccessMessage(payload) {
      const result = payload && payload.event_edit_result ? payload.event_edit_result : null
      const appliedFields = result && Array.isArray(result.applied_fields) ? result.applied_fields : []

      if (appliedFields.length === 0) {
        return 'No habia cambios nuevos por guardar.'
      }

      return `Evento actualizado al instante en ${this.formatFieldList(appliedFields.slice(0, 3))}.`
    },

    resetForm() {
      this.hydrateFormFromEvent(this.event)
      this.errorMessage = ''
      this.successMessage = ''
    },

    launchEvent() {
      if (!this.canLaunchEvent || this.launching) {
        return
      }

      this.launching = true
      this.errorMessage = ''
      this.successMessage = ''

      apiService(`/api/host/events/${this.eventId}/launch/`, 'POST', {}).then((data) => {
        if (data && !data.err && data.id) {
          this.successMessage = 'Evento lanzado. Ya quedo visible para compra.'
          this.$emit('event-updated', data)
          return
        }

        this.errorMessage = this.extractErrorMessage(data, 'No pudimos lanzar el evento.')
      }).finally(() => {
        this.launching = false
      })
    },

    async submitChanges() {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.isDirty) {
        this.successMessage = 'No hay cambios nuevos por guardar.'
        return
      }

      if (isLocalRangeInvalid(this.form.start_date, this.form.end_date)) {
        this.errorMessage = 'La fecha de fin no puede ser anterior a la fecha de inicio.'
        return
      }

      this.saving = true
      try {
        const payload = await this.buildPayload()
        const data = await apiServiceForm(`/api/host/events/${this.eventId}/`, 'PATCH', payload, { timeoutMs: 60000 })

        if (data && !data.err && data.id) {
          this.successMessage = this.buildSuccessMessage(data)
          this.$emit('event-updated', data)
          return
        }

        this.errorMessage = this.extractErrorMessage(data)
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : 'No pudimos preparar la portada para mobile.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.event-edit-shell {
  display: grid;
}

.event-edit-panel {
  display: grid;
  gap: 20px;
}

.event-edit-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: 18px;
  align-items: stretch;
}

.event-edit-hero__copy,
.event-edit-hero__media,
.event-edit-card,
.event-edit-footer,
.event-edit-review-note {
  border-radius: 26px;
  border: 1px solid rgba(194, 212, 224, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.event-edit-hero__copy {
  display: grid;
  gap: 14px;
  padding: 22px;
}

.event-edit-hero__badges {
  align-items: flex-start;
}

.event-edit-hero__media {
  overflow: hidden;
}

.event-edit-file-input {
  display: none;
}

.event-edit-banner {
  position: relative;
  width: 100%;
  min-height: 280px;
  padding: 0;
  border: 0;
  border-radius: 26px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(132, 229, 217, 0.14), transparent 22%),
    linear-gradient(180deg, rgba(8, 13, 19, 0.92), rgba(7, 9, 14, 0.98));
  cursor: pointer;
}

.event-edit-banner.has-soft-fill::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--event-edit-banner-fill);
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  transform: scale(1.08);
  filter: blur(26px) brightness(0.58) saturate(0.88);
}

.event-edit-banner.has-image {
  min-height: 320px;
}

.event-edit-banner.is-dragging {
  box-shadow: 0 0 0 1px rgba(140, 243, 232, 0.24), 0 24px 48px rgba(0, 0, 0, 0.18);
}

.event-edit-banner__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  position: relative;
  z-index: 1;
}

.event-edit-banner.has-soft-fill .event-edit-banner__image {
  object-fit: contain;
}

.event-edit-banner__placeholder {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  min-height: 280px;
  padding: 28px;
  color: rgba(230, 238, 246, 0.82);
  text-align: center;
  position: relative;
  z-index: 1;
}

.event-edit-banner__placeholder i {
  font-size: 1.8rem;
}

.event-edit-banner__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(6, 10, 14, 0.04), rgba(6, 10, 14, 0.72));
  z-index: 2;
}

.event-edit-banner__copy {
  position: absolute;
  right: 18px;
  bottom: 18px;
  left: 18px;
  display: grid;
  gap: 4px;
  color: #eff7ff;
  text-align: left;
  z-index: 3;
}

.event-edit-banner__copy span {
  font-size: 1rem;
  font-weight: 700;
}

.event-edit-banner__copy small {
  color: rgba(226, 236, 244, 0.78);
}

.event-edit-banner__actions {
  margin-top: auto;
}

.event-edit-review-note {
  display: grid;
  gap: 8px;
  padding: 18px 20px;
}

.event-edit-review-note span {
  color: rgba(202, 214, 223, 0.7);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.event-edit-review-note strong {
  color: #eef4ff;
}

.event-edit-review-note p {
  margin: 0;
  color: rgba(222, 231, 241, 0.78);
  line-height: 1.65;
}

.event-edit-review-note.is-warning {
  background: rgba(255, 191, 71, 0.08);
  border-color: rgba(255, 191, 71, 0.22);
}

.event-edit-review-note.is-danger {
  background: rgba(255, 107, 107, 0.08);
  border-color: rgba(255, 107, 107, 0.18);
}

.event-edit-review-note.is-neutral {
  background: rgba(255, 255, 255, 0.03);
}

.event-edit-form {
  gap: 18px;
}

.event-edit-card {
  display: grid;
  gap: 16px;
  padding: 20px;
}

.event-edit-card__head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.event-edit-card__head small {
  display: block;
  color: rgba(202, 214, 223, 0.7);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.event-edit-card__head strong {
  color: #eef4ff;
  font-size: 1.02rem;
}

.event-edit-card__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  color: rgba(214, 225, 236, 0.88);
  font-size: 0.76rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(194, 212, 224, 0.1);
}

.event-edit-grid {
  gap: 14px;
}

.event-edit-optional,
.event-edit-optional-stack {
  display: grid;
  gap: 12px;
}

.event-edit-optional-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.035);
  color: #eef4ff;
  text-align: left;
}

.event-edit-optional-toggle span {
  display: grid;
  gap: 4px;
}

.event-edit-optional-toggle strong {
  color: #eef4ff;
  font-size: 0.95rem;
}

.event-edit-optional-toggle small {
  color: rgba(214, 225, 236, 0.72);
  line-height: 1.5;
}

.event-edit-optional-card {
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.event-edit-cta-presets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}

.event-edit-cta-preset {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(221, 230, 239, 0.84);
  text-align: left;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.event-edit-cta-preset:hover {
  transform: translateY(-1px);
  border-color: rgba(140, 243, 232, 0.22);
}

.event-edit-cta-preset.is-active {
  border-color: rgba(140, 243, 232, 0.34);
  background: rgba(140, 243, 232, 0.08);
  color: #eefcff;
}

.event-edit-cta-preset strong {
  color: inherit;
  font-size: 0.92rem;
}

.event-edit-cta-preset small {
  color: rgba(214, 225, 236, 0.7);
  line-height: 1.45;
}

.event-edit-cta-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.event-edit-visibility-strip {
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 212, 224, 0.08);
}

.event-edit-visibility-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.event-edit-visibility-copy small {
  color: rgba(202, 214, 223, 0.72);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.event-edit-visibility-copy strong {
  color: #eef4ff;
  overflow-wrap: anywhere;
}

.event-edit-visibility-copy span {
  color: rgba(214, 225, 236, 0.76);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.event-edit-location-helper {
  margin-top: 10px;
}

.event-edit-link-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(214, 221, 232, 0.78);
  text-decoration: underline;
}

.event-edit-link-button--pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(194, 212, 224, 0.12);
  background: rgba(11, 19, 24, 0.88);
  color: #eef4ff;
  text-decoration: none;
}

.event-edit-link-button--pill.is-active {
  border-color: rgba(255, 206, 115, 0.24);
  background: rgba(255, 206, 115, 0.1);
  color: #ffe2a7;
}

.event-edit-venue-composer {
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 212, 224, 0.08);
}

.event-edit-venue-composer__grid,
.event-edit-venue-composer__actions {
  display: grid;
  gap: 12px;
}

.event-edit-mode-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.event-edit-mode-card {
  flex: 1 1 240px;
  display: grid;
  gap: 10px;
  min-width: 0;
  min-height: 100%;
  padding: 16px;
  border-radius: 22px;
  border: 1px solid rgba(194, 212, 224, 0.12);
  background: rgba(10, 15, 22, 0.9);
  color: rgba(220, 229, 239, 0.84);
  text-align: left;
  overflow: hidden;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.event-edit-mode-card:hover {
  transform: translateY(-1px);
  border-color: rgba(140, 243, 232, 0.24);
}

.event-edit-mode-card.is-active {
  border-color: rgba(140, 243, 232, 0.34);
  box-shadow: 0 18px 30px rgba(5, 11, 19, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(18, 24, 32, 0.98), rgba(10, 14, 21, 0.98));
}

.event-edit-mode-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(140, 243, 232, 0.12);
  color: #8cf3e8;
}

.event-edit-mode-card__icon--alt {
  background: rgba(255, 206, 115, 0.12);
  color: #ffce73;
}

.event-edit-mode-card__badge {
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(140, 243, 232, 0.12);
  color: #c8fff8;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.event-edit-mode-card__badge--alt {
  background: rgba(255, 206, 115, 0.12);
  color: #ffe2a7;
}

.event-edit-mode-card strong {
  color: #eef4ff;
  font-size: 1.02rem;
  overflow-wrap: anywhere;
}

.event-edit-mode-card p {
  margin: 0;
  color: rgba(214, 225, 236, 0.78);
  line-height: 1.6;
  white-space: normal;
  overflow-wrap: anywhere;
}

.event-edit-launch-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  min-width: 0;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(194, 212, 224, 0.1);
}

.event-edit-launch-card > div {
  min-width: 0;
}

.event-edit-launch-card strong {
  display: block;
  color: #eef4ff;
  overflow-wrap: anywhere;
}

.event-edit-launch-card p {
  margin: 8px 0 0;
  color: rgba(214, 225, 236, 0.82);
  line-height: 1.6;
  white-space: normal;
  overflow-wrap: anywhere;
}

.event-edit-launch-card__hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(226, 236, 244, 0.78);
  line-height: 1.45;
  text-align: center;
}

.event-edit-launch-card__hint strong {
  display: inline;
}

.event-edit-launch-card.is-ready {
  background: rgba(140, 243, 232, 0.08);
  border-color: rgba(140, 243, 232, 0.18);
}

.event-edit-launch-card.is-live {
  background: rgba(120, 184, 255, 0.08);
  border-color: rgba(120, 184, 255, 0.18);
}

.event-edit-launch-card.is-waiting {
  background: rgba(255, 255, 255, 0.03);
}

.event-edit-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
}

.event-edit-footer__summary {
  display: grid;
  gap: 4px;
}

.event-edit-footer__summary small {
  color: rgba(202, 214, 223, 0.72);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.event-edit-footer__summary strong {
  color: #eef4ff;
}

.event-edit-footer__summary span {
  color: rgba(214, 225, 236, 0.76);
}

.event-edit-shell .workspace-button--ghost {
  background: rgba(11, 19, 24, 0.9) !important;
  border-color: rgba(194, 212, 224, 0.12) !important;
}

.event-edit-shell :deep(.workspace-field__note) {
  color: rgba(208, 219, 238, 0.68);
  line-height: 1.5;
}

@media (max-width: 980px) {
  .event-edit-hero {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .event-edit-card,
  .event-edit-hero__copy,
  .event-edit-footer,
  .event-edit-review-note {
    padding: 16px;
  }

  .event-edit-card__head,
  .event-edit-launch-card,
  .event-edit-footer {
    display: grid;
  }

  .event-edit-mode-cards {
    display: grid;
    grid-template-columns: 1fr;
  }

  .event-edit-mode-card,
  .event-edit-visibility-strip,
  .event-edit-launch-card {
    padding: 14px;
  }

  .event-edit-mode-card {
    gap: 8px;
  }

  .event-edit-mode-card p,
  .event-edit-launch-card p,
  .event-edit-visibility-copy span {
    font-size: 0.96rem;
    line-height: 1.45;
  }

  .event-edit-link-button--pill,
  .event-edit-launch-card .workspace-button {
    width: 100%;
    justify-content: center;
  }

  .event-edit-banner,
  .event-edit-banner.has-image {
    min-height: 240px;
  }
}
</style>
